import { Button, Box, Chip, Divider, Stack, Typography, useTheme } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import ServerSideDataTable from "@components/ServerSideDataTable";
import { urls } from "@routes";
import { format } from "date-fns";
import {useSnackbar} from "notistack";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

type EmailVerificationStatus = "processing" | "completed" | "failed";

interface ContactListItem {
    uid: string
    name: string
    workspace_id: number
    workspace_name: string
    user_id: number
    user_email: string
    created_on: number
    status: string
    email_verification_status: EmailVerificationStatus
    verification_task_uid: string | null
    contacts_count: number
    processed_contacts_count: number
}

interface PageData {
    total: number
    total_pages: number
    current_page: number
    contact_lists: ContactListItem[]
}

interface ProgressItem {
    uid: string
    email_verification_status: EmailVerificationStatus
    processed_contacts_count: number
    total_contacts_count: number
}

export default function AdminEmailVerificationResults() {
    const theme = useTheme();
    const { user } = useAuth();
    const {enqueueSnackbar} = useSnackbar();
    const [pageData, setPageData] = useState<PageData>();
    const [exportLoading, setExportLoading] = useState(false);

    const pageDataQuery = useQuery({
        queryKey: ["allEmailVerificationLists"],
        queryFn: () => authenticateAndFetchData(
            `/admin/admin-get-all-email-verification-contact-lists/?page=1&page_size=200`
        ),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            setPageData(pageDataQuery.data.data as PageData);
        }
    }, [pageDataQuery.data]);

    // Poll the moving progress counters. 
    const processingUids = pageData?.contact_lists
        .filter((cl) => cl.email_verification_status === "processing")
        .map((cl) => cl.uid) ?? [];

    const progressQuery = useQuery({
        queryKey: ["allEmailVerificationProgress", processingUids],
        queryFn: () => authenticateAndPostData(
            "/admin/admin-email-verification-progress/",
            { uids: processingUids }
        ),
        enabled: processingUids.length > 0,
        gcTime: 0,
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        refetchInterval: 5000,
    });

    useEffect(() => {
        const progress = progressQuery.data?.data?.progress as ProgressItem[] | undefined;
        if (!progress) return;
        let anyCompleted = false;
        setPageData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                contact_lists: prev.contact_lists.map((cl) => {
                    const p = progress.find((x) => x.uid === cl.uid);
                    if (!p) return cl;
                    if (p.email_verification_status !== "processing") anyCompleted = true;
                    return {
                        ...cl,
                        processed_contacts_count: p.processed_contacts_count,
                        contacts_count: p.total_contacts_count ?? cl.contacts_count,
                        email_verification_status: p.email_verification_status,
                    };
                }),
            };
        });
        // A list just finished — pull authoritative final row data once. Polling stops on its own
        // when no processing rows remain.
        if (anyCompleted) pageDataQuery.refetch();
    }, [progressQuery.data]);

    function timestampFormatter(params: ValueFormatterParams) {
        if (params.value !== null) {
            return format(new Date(params.value), "do MMM y, HH:mm:ss");
        }
        return "---";
    }

    const UserEmailLink = (customProps: CustomCellRendererProps) => (
        <Link
            to={urls["adminUserDetails"].replace(":userId", customProps.data["user_id"]?.toString() || "")}
            style={{ color: theme.palette.text.primary }}>
            {customProps.data["user_email"]}
        </Link>
    );

    const ListNameLink = (customProps: CustomCellRendererProps) => (
        <Link
            to={urls["adminUserContactListDetails"]
                .replace(":userId", customProps.data["user_id"]?.toString() || "")
                .replace(":contactListUID", customProps.data["uid"])}
            style={{ color: theme.palette.text.primary }}>
            {customProps.data["name"]}
        </Link>
    );

    const VerificationStatusBadge = (customProps: CustomCellRendererProps) => {
        if (customProps.value === "processing") {
            return <Chip size="small" color="info" label="Processing" />;
        } else if (customProps.value === "completed") {
            return <Chip size="small" color="success" label="Completed" />;
        } else if (customProps.value === "failed") {
            return <Chip size="small" color="error" label="Failed" />;
        }
        return <Chip size="small" color="default" label={customProps.value} />;
    };

    const VerifiedCountCell = (customProps: CustomCellRendererProps) => {
        const total: number = customProps.data["contacts_count"] ?? 0;
        const verified: number = customProps.data["processed_contacts_count"] ?? 0;
        return <span>{verified} / {total}</span>;
    };

    const columnDefs: ColDef[] = [
        { field: "user_email", headerName: "User Email", cellRenderer: UserEmailLink, flex: 2 },
        { field: "name", headerName: "List Name", cellRenderer: ListNameLink, flex: 2 },
        { field: "workspace_name", headerName: "Workspace", flex: 1 },
        {
            field: "created_on",
            headerName: "Created On",
            valueFormatter: timestampFormatter,
            flex: 2,
        },
        { field: "contacts_count", headerName: "Total Contacts" },
        {
            field: "email_verification_status",
            headerName: "Verification Status",
            cellRenderer: VerificationStatusBadge,
            sortable: false,
        },
        {
            field: "processed_contacts_count",
            headerName: "Verified Contacts",
            cellRenderer: VerifiedCountCell,
            sortable: false,
        },
    ];

    const EmailVerificationColumns: CSVColumn<ContactListItem>[] = [
        { header: "User Email", accessor: "user_email" },
        { header: "List Name", accessor: "name" },
        { header: "Workspace", accessor: "workspace_name" },
        { 
            header: "Created On", 
            accessor: (row) => 
                row.created_on 
                    ? format(new Date(row.created_on), "yyyy-MM-dd HH:mm:ss") 
                    : "—" 
        },
        { header: "Total Contacts", accessor: (row) => row.contacts_count ?? 0 },
        { header: "Verified Contacts", accessor: (row) => row.processed_contacts_count ?? 0 },
        { 
            header: "Progress", 
            accessor: (row) => `${row.processed_contacts_count ?? 0} / ${row.contacts_count ?? 0}` 
        },
        { 
            header: "Verification Status", 
            accessor: (row) => 
                row.email_verification_status 
                    ? row.email_verification_status.charAt(0).toUpperCase() + row.email_verification_status.slice(1) 
                    : "—" 
        },
        { header: "List UID", accessor: "uid" },
        { header: "User ID", accessor: "user_id" },
        { header: "Workspace ID", accessor: "workspace_id" },
        { header: "Verification Task UID", accessor: (row) => row.verification_task_uid ?? "—" },
    ];

    // 2. Export Click Handler
    const handleExportEmailVerificationResults = () => {
        setExportLoading(true);

        try {
            const contactListsToExport = pageData?.contact_lists || [];
            const success = exportToCSV(
                contactListsToExport, 
                EmailVerificationColumns,                 
                getExportFilename(user?.email ?? "unknown", "AllEmailVerificationResults")
            );

            if (success) {
                enqueueSnackbar("Email verification results exported!", { variant: "success" });
            } else {
                enqueueSnackbar("No data available to export.", { variant: "error" });
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Error while exporting email verification results.", { variant: "error" });
        } finally {
            setExportLoading(false);
        }
    };

    // 3. Export Button Component
    const ExportEmailVerificationResultsCsvButton = () => {
        return (
            <Button 
                startIcon={<Downloading />}
                variant="contained"
                color="primary"
                onClick={handleExportEmailVerificationResults}
                disabled={exportLoading}
            >
                {exportLoading ? "Exporting..." : "Export"}
            </Button>
        );
    };

    if (pageDataQuery.isLoading) {
        return <PageLoading />;
    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />;
    } else if (pageData) {
        return (
            <Stack direction="column" spacing={4} sx={{ pb: 4, height: "100%" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography fontWeight="bold" variant="h5">
                        Email Verification Results
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        ({pageData.total} total)
                    </Typography>
                </Stack>
                <Divider />
                <Box sx={{ height: "100%", maxHeight: 800 }}>
                    <ServerSideDataTable
                        columns={columnDefs}
                        rows={pageData.contact_lists}
                        getRowId={(p) => p.data.uid}
                        noRowsText="No email verification submissions found"
                        loading={pageDataQuery.isFetching}
                        actions={<ExportEmailVerificationResultsCsvButton />}
                    />
                </Box>
            </Stack>
        );
    } else {
        return <></>;
    }
}
